import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { ClickAwayListener } from '@mui/material';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/browser';

import LoggedInUserContext from '../../context/LoggedInUserContext';
import User from '../../Model/User';

import {
  Wrapper,
  StyledPopper,
  MenuList,
  MenuButton,
} from './styles';
import texts from './texts.json';

const escapeHtml = (value) => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// coachNotes/workoutPreferences are rendered by Quill, so plain-text
// selections need to be emitted as <p>/<br> HTML to round-trip through
// the editor.
const buildHtmlToAppend = (rawText) => {
  const trimmed = (rawText || '').trim();
  if (!trimmed) {
    return '';
  }
  const paragraphs = trimmed.split(/\n{2,}/).map((paragraph) => {
    const escaped = escapeHtml(paragraph).replace(/\n/g, '<br>');
    return `<p>${escaped}</p>`;
  });
  return paragraphs.join('');
};

const AddToNotesMenu = ({
  clientUserDoc,
  children,
  disabled,
  showAddToWorkoutPreferences,
}) => {
  const wrapperRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { userDoc: loggedInUserDoc } = useContext(LoggedInUserContext);
  const editorDisplayName = (loggedInUserDoc?.name || '').trim() || loggedInUserDoc?.email || '';

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    setSelectedText('');
  }, []);

  // MUI Popper accepts any object exposing getBoundingClientRect as an
  // anchorEl, so we wrap the selection rect instead of a real DOM node.
  const buildVirtualAnchor = useCallback((clientRect) => ({
    getBoundingClientRect: () => clientRect,
    nodeType: 1,
  }), []);

  const isSelectionWithinWrapper = useCallback((selection) => {
    const wrapperNode = wrapperRef.current;
    if (!wrapperNode || selection.rangeCount === 0) {
      return false;
    }
    const range = selection.getRangeAt(0);
    // Make sure both the anchor and focus are inside the wrapper.
    return (
      wrapperNode.contains(range.startContainer)
      && wrapperNode.contains(range.endContainer)
    );
  }, []);

  const openMenuAtSelection = useCallback((overridePosition = null) => {
    if (disabled || !clientUserDoc) {
      return;
    }

    const selection = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!selection || selection.isCollapsed) {
      closeMenu();
      return;
    }

    const text = selection.toString();
    if (!text || !text.trim()) {
      closeMenu();
      return;
    }

    if (!isSelectionWithinWrapper(selection)) {
      closeMenu();
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // When triggered via contextmenu, prefer the pointer position so the
    // menu appears right under the cursor. Otherwise anchor to the end of
    // the selection rect.
    const anchorRect = overridePosition
      ? {
        top: overridePosition.y,
        left: overridePosition.x,
        bottom: overridePosition.y,
        right: overridePosition.x,
        width: 0,
        height: 0,
        x: overridePosition.x,
        y: overridePosition.y,
      }
      : rect;

    setSelectedText(text);
    setAnchorEl(buildVirtualAnchor(anchorRect));
  }, [
    disabled,
    clientUserDoc,
    isSelectionWithinWrapper,
    buildVirtualAnchor,
    closeMenu,
  ]);

  const handleMouseUp = useCallback(() => {
    // Defer to the next tick so that the browser has finalised the selection.
    window.setTimeout(() => openMenuAtSelection(), 0);
  }, [openMenuAtSelection]);

  const handleContextMenu = useCallback((event) => {
    const selection = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!selection || selection.isCollapsed) {
      return;
    }
    const text = selection.toString();
    if (!text || !text.trim()) {
      return;
    }
    if (!isSelectionWithinWrapper(selection)) {
      return;
    }
    event.preventDefault();
    openMenuAtSelection({ x: event.clientX, y: event.clientY });
  }, [
    isSelectionWithinWrapper,
    openMenuAtSelection,
  ]);

  const handleAddToNotes = useCallback(async () => {
    if (!clientUserDoc || !selectedText || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const htmlToAppend = buildHtmlToAppend(selectedText);
      if (!htmlToAppend) {
        closeMenu();
        return;
      }

      await clientUserDoc.updateCoachNotes(htmlToAppend, editorDisplayName, { append: true });
      toast.success(texts.addedToNotes, { position: 'top-right', autoClose: 3000 });

      // Clear the browser selection so the menu dismisses cleanly.
      if (typeof window !== 'undefined') {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error(texts.failedToAddToNotes, { position: 'top-right', autoClose: 4000 });
    } finally {
      setIsSaving(false);
      closeMenu();
    }
  }, [
    clientUserDoc,
    selectedText,
    isSaving,
    closeMenu,
    editorDisplayName,
  ]);

  const handleAddToWorkoutPreferences = useCallback(async () => {
    if (!clientUserDoc || !selectedText || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const htmlToAppend = buildHtmlToAppend(selectedText);
      if (!htmlToAppend) {
        closeMenu();
        return;
      }
      await clientUserDoc.updateWorkoutPreferences(htmlToAppend, editorDisplayName, { append: true });
      toast.success(texts.addedToWorkoutPreferences, { position: 'top-right', autoClose: 3000 });

      if (typeof window !== 'undefined') {
        const selection = window.getSelection();
        if (selection) selection.removeAllRanges();
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error(texts.failedToAddToWorkoutPreferences, { position: 'top-right', autoClose: 4000 });
    } finally {
      setIsSaving(false);
      closeMenu();
    }
  }, [clientUserDoc, selectedText, isSaving, closeMenu, editorDisplayName]);

  useEffect(() => {
    if (!anchorEl) {
      return undefined;
    }
    const handleScroll = () => closeMenu();
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [anchorEl, closeMenu]);

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseUp={handleMouseUp}
      onContextMenu={handleContextMenu}
    >
      {children}
      {!!anchorEl && !!clientUserDoc && (
        <ClickAwayListener onClickAway={closeMenu}>
          <StyledPopper
            open
            anchorEl={anchorEl}
            placement="bottom-start"
            modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
          >
            <MenuList>
              <MenuButton
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={handleAddToNotes}
                disabled={isSaving}
              >
                {texts.addToNotes}
              </MenuButton>
              {showAddToWorkoutPreferences && (
                <MenuButton
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={handleAddToWorkoutPreferences}
                  disabled={isSaving}
                >
                  {texts.addToWorkoutPreferences}
                </MenuButton>
              )}
            </MenuList>
          </StyledPopper>
        </ClickAwayListener>
      )}
    </Wrapper>
  );
};

AddToNotesMenu.propTypes = {
  clientUserDoc: PropTypes.instanceOf(User),
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  showAddToWorkoutPreferences: PropTypes.bool,
};

AddToNotesMenu.defaultProps = {
  clientUserDoc: null,
  disabled: false,
  showAddToWorkoutPreferences: false,
};

export default AddToNotesMenu;
